import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

// Import Botpoison with type assertion to avoid TypeScript errors
const Botpoison = require('@botpoison/browser');

// Create Botpoison instance
const botpoison = new Botpoison({
  publicKey: "pk_90c738f9-ee74-494d-8e7a-d48d5c347093"
});

// Styled components for layout and responsiveness
const ContactSection = styled.section`
  width: 100%;
  background-color: #4a4a4a;
  padding: 4rem 2rem;
  color: white;
`;

const ContactContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const ContactHeader = styled.div`
  margin-bottom: 3rem;
  text-align: center;
`;

const ContactTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 400;
  margin-bottom: 1rem;
  color: white;
`;

const ContactDivider = styled.div`
  height: 4px;
  width: 80px;
  background-color: #d4af37;
  margin: 0 auto;
`;

const ContactLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  
  @media (max-width: 967px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const ContactFormColumn = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const ContactForm = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 3rem;
`;

const ContactFormGroup = styled.div`
  margin-bottom: 1.5rem;
  width: 100%;
`;

const ContactFormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  
  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const ContactFormLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 400;
  color: white;
  font-size: 1rem;
  text-align: left;
`;

const ContactFormInput = styled.input`
  width: 100%;
  padding: 15px 20px;
  border: none;
  border-radius: 25px;
  font-family: inherit;
  font-size: 16px;
  background-color: #f5f5f5;
  color: #333;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    background-color: white;
    box-shadow: 0 0 0 2px #d4af37;
  }
  
  &::placeholder {
    color: #999;
  }
`;

const ContactFormTextarea = styled.textarea`
  width: 100%;
  padding: 15px 20px;
  border: none;
  border-radius: 25px;
  font-family: inherit;
  font-size: 16px;
  background-color: #f5f5f5;
  color: #333;
  min-height: 120px;
  resize: vertical;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    background-color: white;
    box-shadow: 0 0 0 2px #d4af37;
  }
  
  &::placeholder {
    color: #999;
  }
`;

const SubmitButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: 1rem;
`;

const ContactFormSubmit = styled.button`
  padding: 15px 40px;
  background-color: #d4af37;
  color: white;
  border: none;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.3s ease;
  min-width: 120px;
  
  &:hover {
    background-color: #b8941f;
    transform: translateY(-2px);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

const ContactInfoPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const ContactInfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ContactInfoLabel = styled.h4`
  color: #d4af37;
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0;
`;

const ContactInfoValue = styled.a`
  color: white;
  font-size: 1rem;
  text-decoration: none;
  
  &:hover {
    color: #d4af37;
  }
`;

const ContactMapColumn = styled.div`
  @media (max-width: 967px) {
    margin-top: 2rem;
  }
`;

const ContactMapContainer = styled.div`
  height: 100%;
  min-height: 500px;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
`;

const ContactMap = styled.div`
  height: 100%;
  
  iframe {
    width: 100%;
    height: 100%;
    border: 0;
  }
`;

const ErrorMessage = styled.div`
  color: #ff6b6b;
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const SuccessContainer = styled.div`
  text-align: center;
  padding: 3rem;
  background-color: rgba(212, 175, 55, 0.1);
  border-radius: 15px;
  border: 2px solid #d4af37;
`;

const SuccessIcon = styled.svg`
  color: #d4af37;
  margin-bottom: 1rem;
`;

const SuccessTitle = styled.h3`
  color: #d4af37;
  font-size: 1.5rem;
  margin-bottom: 1rem;
`;

const SuccessMessage = styled.p`
  color: white;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const SuccessButton = styled.button`
  padding: 12px 30px;
  background-color: #d4af37;
  color: white;
  border: none;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #b8941f;
  }
`;

interface ContactFormProps {
  className?: string;
}

interface FormData {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  subject?: string;
  message?: string;
  form?: string;
}

interface Location {
  id: number;
  name: string;
  address: string;
  lat: number;
  lng: number;
}

const LOCATIONS: Location[] = [
  {
    id: 1,
    name: "Los Angeles Office",
    address: "123 Airport Way, Los Angeles, CA 90045",
    lat: 33.9416,
    lng: -118.4085
  },
  {
    id: 2,
    name: "New York Office",
    address: "456 Aviation Blvd, New York, NY 10001",
    lat: 40.7128,
    lng: -74.0060
  },
  {
    id: 3,
    name: "Miami Office",
    address: "789 Flight Lane, Miami, FL 33126",
    lat: 25.7617,
    lng: -80.1918
  }
];

const ContactFormComponent: React.FC<ContactFormProps> = ({ className = '' }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing again
    if (errors[name as keyof FormData]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('contact_name_required') as string;
    }

    if (!formData.email.trim()) {
      newErrors.email = t('contact_email_required') as string;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('contact_email_invalid') as string;
    }

    if (!formData.subject.trim()) {
      newErrors.subject = t('contact_subject_required') as string;
    }

    if (!formData.message.trim()) {
      newErrors.message = t('contact_message_required') as string;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Process Botpoison challenge
      const { solution } = await botpoison.challenge();

      // Submit to Formspark
      const response = await fetch('https://submit-form.com/fBQhhh8X7', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          subject: formData.subject,
          message: formData.message,
          _botpoison: solution, // Include Botpoison solution
          _email: {
            to: ['sales@airlinecrewtrans.com', 'warren.r.huffman@gmail.com'],
            subject: `Contact Form: ${formData.subject}`,
            from: formData.email
          }
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      });
    } catch (error) {
      console.error('Form submission error:', error);
      setErrors(prev => ({
        ...prev,
        form: t('contact_error', 'There was an error submitting the form. Please try again.') as string
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <ContactSection className={className}>
        <ContactContainer>
          <SuccessContainer>
            <SuccessIcon viewBox="0 0 24 24" width="64" height="64">
              <circle cx="12" cy="12" r="11" fill="none" stroke="currentColor" strokeWidth="2" />
              <path d="M7 13l3 3 7-7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </SuccessIcon>
            <SuccessTitle>{t('contact_thank_you')}</SuccessTitle>
            <SuccessMessage>
              {t('contact_success_message')}
            </SuccessMessage>
            <SuccessButton
              onClick={() => setIsSubmitted(false)}
              type="button"
            >
              {t('contact_send_another')}
            </SuccessButton>
          </SuccessContainer>
        </ContactContainer>
      </ContactSection>
    );
  }

  return (
    <ContactSection className={className}>
      <ContactContainer>
        <ContactHeader>
          <ContactTitle>{t('contact_title')}</ContactTitle>
          <ContactDivider />
        </ContactHeader>

        <ContactLayout>
          {/* Left column - Contact Form */}
          <ContactFormColumn>
            {errors.form && (
              <ErrorMessage>
                {errors.form}
              </ErrorMessage>
            )}

            <ContactForm onSubmit={handleSubmit}>
              <ContactFormGroup>
                <ContactFormLabel htmlFor="name">{t('contact_name')}</ContactFormLabel>
                <ContactFormInput
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t('contact_name_placeholder') as string}
                  autoComplete="name"
                  autoCapitalize="words"
                />
                {errors.name && (
                  <ErrorMessage>{errors.name}</ErrorMessage>
                )}
              </ContactFormGroup>

              <ContactFormGroup>
                <ContactFormLabel htmlFor="email">{t('contact_email')}</ContactFormLabel>
                <ContactFormInput
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t('contact_email_placeholder') as string}
                  autoComplete="email"
                  autoCapitalize="off"
                  inputMode="email"
                />
                {errors.email && (
                  <ErrorMessage>{errors.email}</ErrorMessage>
                )}
              </ContactFormGroup>

              <ContactFormGroup>
                <ContactFormLabel htmlFor="phone">{t('contact_phone')}</ContactFormLabel>
                <ContactFormInput
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder={t('contact_phone_placeholder') as string}
                  autoComplete="tel"
                  inputMode="tel"
                  pattern="[0-9+\-\s()]+"
                />
              </ContactFormGroup>

              <ContactFormGroup>
                <ContactFormLabel htmlFor="subject">{t('contact_subject')}</ContactFormLabel>
                <ContactFormInput
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder={t('contact_subject_placeholder') as string}
                  autoComplete="off"
                />
                {errors.subject && (
                  <ErrorMessage>{errors.subject}</ErrorMessage>
                )}
              </ContactFormGroup>

              <ContactFormGroup>
                <ContactFormLabel htmlFor="message">{t('contact_message')}</ContactFormLabel>
                <ContactFormTextarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={t('contact_message_placeholder') as string}
                  rows={5}
                  autoCapitalize="sentences"
                ></ContactFormTextarea>
                {errors.message && (
                  <ErrorMessage>{errors.message}</ErrorMessage>
                )}
              </ContactFormGroup>

              <SubmitButtonContainer>
                <ContactFormSubmit
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t('contact_sending') : t('contact_submit')}
                </ContactFormSubmit>
              </SubmitButtonContainer>
            </ContactForm>

            <ContactInfoPanel>
              <ContactInfoItem>
                <ContactInfoLabel>Email</ContactInfoLabel>
                <ContactInfoValue href="mailto:sales@airlinecrewtrans.com">
                  sales@airlinecrewtrans.com
                </ContactInfoValue>
              </ContactInfoItem>

              <ContactInfoItem>
                <ContactInfoLabel>Phone</ContactInfoLabel>
                <ContactInfoValue href="tel:+16502259300">
                  +1 (650) 225-9300
                </ContactInfoValue>
              </ContactInfoItem>
            </ContactInfoPanel>
          </ContactFormColumn>

          {/* Right column - Map */}
          <ContactMapColumn>
            <ContactMapContainer>
              <ContactMap>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3164.289559068282!2d-122.42294698469047!3d37.53088797980302!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x808f9f5acdf48383%3A0x91ebd69a23112095!2s199%20San%20Bruno%20Ave%20W%2C%20San%20Bruno%2C%20CA%2094066!5e0!3m2!1sen!2sus!4v1716237118813!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Our Locations"
                ></iframe>
              </ContactMap>
            </ContactMapContainer>
          </ContactMapColumn>
        </ContactLayout>
      </ContactContainer>
    </ContactSection>
  );
};

export default ContactFormComponent; 